import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locations, setLocations] = useState([]);

  // Create axios instance that updates with token
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
    });
    
    // Add request interceptor to always include latest token
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    return instance;
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await api.get('/locations');
      setLocations(res.data);
      if (res.data.length > 0) {
        setCurrentLocation(prev => prev || res.data[0]);
      }
      return res.data;
    } catch (err) {
      console.error('Error fetching locations:', err);
      return [];
    }
  }, [api]);

  const fetchUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      await fetchLocations();
    } catch (err) {
      console.error('Auth error:', err);
      // Clear invalid token
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setCurrentLocation(null);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, [api, fetchLocations]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Fetch locations after login
    await fetchLocations();
    return userData;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Fetch locations after registration
    await fetchLocations();
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCurrentLocation(null);
    setLocations([]);
  };

  const seedDemoData = async () => {
    try {
      const res = await api.post('/seed-demo-data');
      const locs = await fetchLocations();
      // Set the first location as current after seeding
      if (locs && locs.length > 0) {
        setCurrentLocation(locs[0]);
      }
      return res.data;
    } catch (err) {
      console.error('Error seeding demo data:', err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      currentLocation,
      locations,
      setCurrentLocation,
      login,
      register,
      logout,
      seedDemoData,
      fetchLocations,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};
